/**
 * sessionModel — WHAT A SESSION-OPENING HOST CONNECTION ACTUALLY SENDS.
 *
 * Four host slots leave the widget and start (or continue) an agent session:
 * `openCapture`, `openMainSession`, `openSession`, and the `openPage` that
 * hands a DELEGATED WebView page a preflight payload it later dispatches as a
 * capture. The strip drew all four as the same dead chip — "host bridge ·
 * <slot> · no UI — the app performs it" — which is true of the PIXELS and
 * false of the interaction: the prompt, the hidden `<vi_prefix>` /
 * `<vi_suffix>` instructions, the capture framing and the session binding are
 * the entire content of that tap, and none of it was readable anywhere.
 *
 * THE RESOLUTION IS THE RUNTIME'S, not a second interpreter. `resolveProps`
 * (viewir/props) against `buildRenderContext` (viewir/context) is byte-for-byte
 * what `interpreter.jsx` does to a connection payload, including the `signal.*`
 * scope. This module only:
 *   · reconstructs the SIGNAL an OptionSelect emits for the CLICKED option
 *     (`{key, value?, ...payload}` — see `filters.jsx` `pick`), because focus
 *     gives one node per rail and not one per option;
 *   · MARKS what did not resolve (`⟨focusEntry.source_session_id⟩`) instead of
 *     letting a miss read as `undefined` or as an empty string, which is the
 *     difference between "this only resolves on the detail page" and "broken";
 *   · ELIDES data: URIs and anything past 2 KB, without which one scene-studio
 *     template option pastes a ~40 KB base64 cover into the card.
 *
 * Pure — no React, no DOM — so the whole model is unit-testable the way
 * `focusModel` is.
 */
import { buildRenderContext } from '../viewir/context';
import { resolveProps } from '../viewir/props';
import { getPath, hasInterp } from '../viewir/paths';
import { stripHiddenTags, hiddenPrefixTag, hiddenSuffixTag } from '../../../utils/text';

/** The host slots that start or continue an agent session. */
export const SESSION_SLOTS = new Set(['openCapture', 'openMainSession', 'openSession', 'openPage']);

const KIND_BY_SLOT = {
  openCapture: 'capture',
  openMainSession: 'main',
  openSession: 'session',
};

/**
 * THE SESSION TYPE THE BACKEND WILL RECORD — `Session.session_type`, the same
 * two values the admin console filters on (`session_center.VALID_SESSION_TYPES`).
 *
 * It is a property of the SLOT, not of the widget: every capture a widget
 * dispatches (and every per-capture session it continues) is a `widget`
 * session, while `openMainSession` reaches the ONE persistent per-instance
 * chat — `widget_main`, which is hidden from the gallery and is where config
 * and "ask about my data" live. Reading which one a button starts off the
 * payload was impossible before: both arrive as the same host chip.
 */
export function sessionTypeOf(slot) {
  return slot === 'openMainSession' ? 'widget_main' : 'widget';
}

/** `⟨some.path⟩` — a reference `markMisses` could not resolve on this page. */
const UNRESOLVED = /^⟨[^⟩]*⟩$/;
export const isUnresolved = (v) => typeof v === 'string' && UNRESOLVED.test(v);

/**
 * A src the studio can actually paint. A `media://` handle is the app's own
 * opaque reference (resolved host-side, never by us — see the no-raw-GCS rule),
 * so it is PRINTED as the handle it is rather than fed to an `<img>` that would
 * silently draw a broken box.
 */
const RENDERABLE = /^(data:|blob:|https?:\/\/|\/)/i;
export const isRenderableSrc = (v) => typeof v === 'string' && RENDERABLE.test(v);

/** The inner text of a `<vi_prefix>` / `<vi_suffix>` block, tags removed. */
export function innerHiddenTag(tag) {
  return String(tag || '')
    .replace(/^<vi_(?:prefix|suffix)>/i, '')
    .replace(/<\/vi_(?:prefix|suffix)>$/i, '')
    .trim();
}

/** How much of a value the collapsed row shows. */
const PREVIEW = 80;
/** Past this, a string is METERED rather than printed (a base64 payload). */
const MAX_STR = 2048;

const INTERP = /\{([^{}]+)\}/g;
/** A `{{TOKEN}}` the delegated page is supposed to fill in before dispatch. */
const MUSTACHE = /\{\{[^{}]+\}\}/g;
/** The typed widget tags a prompt carries (`#widget_append_<id>`). */
const WIDGET_TAG = /#widget_[A-Za-z0-9_-]+/g;

const kb = (n) => `${n < 1024 ? 1 : Math.round(n / 1024)} KB`;

/**
 * A string as the card may print it.
 *
 * MANDATORY, not cosmetic: scene-studio's template options each carry a
 * `cover` that is a ~40 KB `data:image/webp;base64,…`, and eleven of those in
 * one card is a browser tab that stops responding — printed, previewed or
 * measured. A data URI is reported as its type and size, which is the only
 * thing about it a reader could act on anyway.
 */
export function elide(value) {
  if (typeof value !== 'string') return value;
  if (value.startsWith('data:')) {
    const mime = /^data:([^;,]*)/.exec(value)?.[1] || '';
    return `data:${mime || 'binary'} · ${kb(value.length)}`;
  }
  if (value.length > MAX_STR) return `${value.slice(0, 240)}… · ${kb(value.length)} elided`;
  return value;
}

/** `elide` through a whole JSON tree — an `args` object is mostly strings. */
export function elideDeep(value) {
  if (typeof value === 'string') return elide(value);
  if (Array.isArray(value)) return value.map(elideDeep);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = elideDeep(v);
    return out;
  }
  return value;
}

/** The page file a payload names, in the three spellings the fleet uses. */
export function fileOf(payload) {
  const raw = payload?.page_file ?? payload?.file ?? payload?.page ?? null;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return raw.trim().split('/').pop();
}

/**
 * The `*.html` pages this bundle DELEGATES to the app's WebView, read off the
 * blueprint's own `first_version.delegated` prose (each entry names its files
 * in backticks). Reading the declaration is what keeps this honest for a
 * bundle that delegates nothing: the set is empty and `openPage` falls back to
 * "does this payload carry a prompt", never to a hardcoded file list.
 */
export function delegatedFiles(blueprint) {
  const out = new Set();
  for (const item of blueprint?.first_version?.delegated || []) {
    for (const m of String(item ?? '').match(/[A-Za-z0-9_.-]+\.html/g) || []) out.add(m);
  }
  return out;
}

/**
 * WHICH KIND OF SESSION a host slot opens, or null when the slot has no
 * session behind it at all (`crudRemove`, `imageTap`, an `openPage` that just
 * moves between two pages of the bundle).
 *
 * `openPage` is the one that has to be decided rather than named: my-wardrobe
 * opens its own board through it (no session), while scene-studio's template
 * rails open a delegated preflight page whose payload IS the capture the app
 * will dispatch. Delegation is DECLARED (`first_version.delegated`), and a
 * payload carrying a prompt is the second reading — either one means a session.
 */
export function sessionKindOf(slot, payload = null, delegated = null) {
  const direct = KIND_BY_SLOT[slot];
  if (direct) return direct;
  if (slot !== 'openPage') return null;
  const file = fileOf(payload);
  const set = delegated instanceof Set ? delegated : new Set(delegated || []);
  if (file && set.has(file)) return 'delegated';
  const prompt = payload?.args?.prompt ?? payload?.prompt;
  return typeof prompt === 'string' && prompt.trim() ? 'delegated' : null;
}

/** A reference in object form — `{$: path}` / `{bind: path}`. */
function refPath(spec) {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return null;
  if (typeof spec.$ === 'string') return spec.$;
  if (typeof spec.bind === 'string') return spec.bind;
  return null;
}

/**
 * `interpolate` (viewir/paths) with ONE difference, and it is the whole point:
 * a slot that resolves to nothing renders `⟨focusEntry.title⟩` instead of the
 * empty string the runtime correctly prints.
 *
 * On an index page `focusEntry` is whatever row folded first, and on a detail
 * page opened with no `page_args` it is the same — so "this came out blank" and
 * "this page has no such row" look identical in the rendered app. In a DIAGRAM
 * they must not: the reader is asking what the app will send, and a silent ''
 * is the one answer that cannot be checked.
 */
function markInterp(str, root) {
  return String(str).replace(INTERP, (_all, expr) => {
    const path = expr.trim();
    const v = getPath(root, path);
    if (v == null) return `⟨${path}⟩`;
    return Array.isArray(v) ? v.filter((x) => x != null).join(', ') : String(v);
  });
}

/**
 * The resolved value, with every UNRESOLVED reference replaced by its own path
 * in ⟨⟩. Walks the TEMPLATE (which knows what was asked for) beside the
 * runtime's answer (which knows what came back), so a value that never was a
 * reference passes through untouched.
 */
function markMisses(raw, resolved, root) {
  const path = refPath(raw);
  if (path) return resolved === undefined || resolved === null ? `⟨${path}⟩` : resolved;
  if (typeof raw === 'string' && hasInterp(raw)) return markInterp(raw, root);
  if (Array.isArray(raw)) {
    return raw.map((v, i) => markMisses(v, Array.isArray(resolved) ? resolved[i] : undefined, root));
  }
  if (raw && typeof raw === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(raw)) out[k] = markMisses(v, resolved?.[k], root);
    return out;
  }
  return resolved === undefined ? raw : resolved;
}

/**
 * The page context, MEMOISED per blueprint × feed × page_args — the same
 * treatment `pageFocusBinding` gives its scan, for the same reason.
 *
 * `buildRenderContext` evaluates every selector over the whole feed, and the
 * strip re-renders on every scroll event (the wires are anchored to boxes that
 * move). Six host cards × two resolutions each × one context apiece would run
 * the data lane a dozen times per scroll frame. The feed is WeakMap-keyed, so
 * a card the studio has moved on from takes its contexts with it.
 */
const CTX_CACHE = new WeakMap();
const NO_FEED = {};

function contextFor(blueprint, feed, pageArgs) {
  const build = () => buildRenderContext({ blueprint, feed, pageArgs: pageArgs || {} });
  if (!blueprint || typeof blueprint !== 'object') return build();
  let byFeed = CTX_CACHE.get(blueprint);
  if (!byFeed) { byFeed = new WeakMap(); CTX_CACHE.set(blueprint, byFeed); }
  const feedKey = feed && typeof feed === 'object' ? feed : NO_FEED;
  let byArgs = byFeed.get(feedKey);
  if (!byArgs) { byArgs = new Map(); byFeed.set(feedKey, byArgs); }
  const argKey = pageArgs ? JSON.stringify(pageArgs) : '';
  if (!byArgs.has(argKey)) byArgs.set(argKey, build());
  return byArgs.get(argKey);
}

/**
 * THE PAYLOAD THE HOST SLOT RECEIVES — the interpreter's own rule (§5):
 * the emitting signal's payload, with the connection's payload map resolved
 * over it against the page context plus a `signal` scope.
 *
 * `optionPayload` is the clicked OptionSelect option's own `payload`, which
 * that component SPREADS at the top level of the signal (`filters.jsx`) — the
 * mechanism that lets an authored template option reach `host.openPage` with
 * no per-option connection.
 */
export function resolveSessionPayload({
  blueprint, feed = {}, pageArgs = null, conn = null,
  optionPayload = null, signalShape = null,
} = {}) {
  const ctx = contextFor(blueprint, feed, pageArgs);
  const base = signalShape && typeof signalShape === 'object' ? { ...signalShape } : {};
  if (optionPayload && typeof optionPayload === 'object' && !Array.isArray(optionPayload)) {
    base.payload = optionPayload;
    Object.assign(base, optionPayload);
  }
  const root = { ...ctx, signal: base };
  const raw = conn?.payload ?? null;
  if (!raw) return { payload: base, ctx, signal: base };
  const resolved = { ...base, ...resolveProps(raw, null, root) };
  const marked = markMisses(raw, resolved, root);
  return { payload: { ...resolved, ...marked }, ctx, signal: base };
}

const CAPTURE_KEYS = [
  'kind', 'intent', 'quick', 'target_tab', 'target_page', 'target_focus', 'target_back', 'open_session',
  'images', 'image_roles', 'required_media_count', 'title',
];
const BINDING_KEYS = ['session_id', 'log_card_id', 'quick_log'];
const DELEGATED_KEYS = [
  'file', 'page_file', 'page', 'type', 'template_id', 'prompt_policy', 'upload_label',
  'materials', 'material_roles', 'steps',
  'composition_reference', 'composition_reference_position', 'composition_reference_role',
];
/** Handled by their own rows, or a duplicate of what was spread out of them. */
const OWN_ROWS = new Set(['prompt', 'args', 'payload', ...CAPTURE_KEYS, ...BINDING_KEYS, ...DELEGATED_KEYS]);

/** One value as the card prints it: elided, and JSON when it is not a string. */
function display(value) {
  if (value === undefined) return '—';
  if (typeof value === 'string') return elide(value);
  if (value === null || typeof value !== 'object') return String(value);
  return JSON.stringify(elideDeep(value), null, 1);
}

function row(group, key, label, value) {
  const text = display(value);
  const flat = text.replace(/\s+/g, ' ').trim();
  return {
    key, group, label, value: text,
    preview: flat.length > PREVIEW ? `${flat.slice(0, PREVIEW)}…` : flat,
    long: flat.length > PREVIEW || text.includes('\n'),
  };
}

/**
 * THE UNFOLDABLE ROWS, in the order a reader asks the questions: what does the
 * user see, what does the model additionally get, what does it join to, how is
 * the capture framed, which session does it land in, and what does a delegated
 * page carry.
 *
 * A payload key with no row of its own still gets one at the end. A diagram
 * that silently drops a field is worse than no diagram: the one thing it must
 * never do is make a reader believe they have seen everything.
 */
export function injectionRows(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const rows = [];
  const prompt = typeof p.prompt === 'string' ? p.prompt : null;
  if (prompt !== null) {
    rows.push(row('prompt', 'prompt', 'prompt · visible', stripHiddenTags(prompt)));
    const pre = hiddenPrefixTag(prompt);
    const suf = hiddenSuffixTag(prompt);
    if (pre) rows.push(row('prompt', 'vi_prefix', 'vi_prefix · hidden', pre));
    if (suf) rows.push(row('prompt', 'vi_suffix', 'vi_suffix · hidden', suf));
    const tags = prompt.match(WIDGET_TAG) || [];
    if (tags.length) rows.push(row('prompt', 'tags', 'widget tags', tags.join(' ')));
    const holes = prompt.match(MUSTACHE) || [];
    if (holes.length) rows.push(row('flag', 'tokens', '⚠ unfilled tokens', holes.join(' ')));
  } else if (p.prompt !== undefined) {
    rows.push(row('prompt', 'prompt', 'prompt · visible', p.prompt));
  }
  for (const k of CAPTURE_KEYS) if (p[k] !== undefined) rows.push(row('capture', k, k, p[k]));
  for (const k of BINDING_KEYS) if (p[k] !== undefined) rows.push(row('binding', k, k, p[k]));
  for (const k of DELEGATED_KEYS) if (p[k] !== undefined) rows.push(row('delegated', k, k, p[k]));
  if (p.args !== undefined) rows.push(row('args', 'args', 'args', p.args));
  for (const k of Object.keys(p)) {
    if (OWN_ROWS.has(k)) continue;
    rows.push(row('extra', k, k, p[k]));
  }
  if (p.payload !== undefined) {
    rows.push(row('extra', 'payload', 'payload · also spread above', p.payload));
  }
  return rows;
}

/**
 * ── THE READABLE VIEW ────────────────────────────────────────────────────────
 *
 * `injectionRows` above is the COMPLETE reading: one row per payload key, in
 * payload order, nothing dropped. It is also, for a scene-studio template, one
 * 4 KB `args` blob printed as JSON — the reader asked "what session does this
 * button start?" and got a serialised object to parse by eye.
 *
 * `sessionSections` answers the question in the order it is actually asked:
 * which template · what does the model read · what is injected around it · which
 * photos travel with it · and then everything else, still complete, still
 * unfoldable. It never REPLACES a key with a prettier one — every key the
 * payload carries ends up in exactly one section.
 *
 * A DELEGATED preflight nests the whole session under `args` (that is the
 * shipped `open_page {file, type, args}` shape), while a capture carries the
 * same fields at the top level. `reader` is the one place that difference
 * lives: `at('prompt')` is the prompt either way.
 */
function reader(payload) {
  const p = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  const args = p.args && typeof p.args === 'object' && !Array.isArray(p.args) ? p.args : null;
  return { p, args, at: (k) => (p[k] !== undefined ? p[k] : args?.[k]) };
}

/** The prompt, split into what the user sees and what only the model gets. */
export function promptOf(payload) {
  const { at } = reader(payload);
  const raw = at('prompt');
  if (raw === undefined) return null;
  if (typeof raw !== 'string') return { visible: display(raw), prefix: '', suffix: '', tags: [], tokens: [] };
  return {
    visible: stripHiddenTags(raw),
    prefix: innerHiddenTag(hiddenPrefixTag(raw)),
    suffix: innerHiddenTag(hiddenSuffixTag(raw)),
    tags: raw.match(WIDGET_TAG) || [],
    tokens: raw.match(MUSTACHE) || [],
  };
}

/**
 * THE PHOTOS THAT TRAVEL WITH THE SESSION, in the order the model receives
 * them — which is the order `image_roles` names them in, and the whole reason
 * both are arrays rather than a set.
 *
 * A slot can be EMPTY for two different reasons and the card must not conflate
 * them: `images` is an unresolved `⟨page_args.role_urls⟩` (the upload form
 * fills it at submit time, so there is nothing to show HERE), or it is a COUNT
 * (`images: 1` — the photo does not exist yet and the seeded capture sheet is
 * what collects it). Both read as "no thumbnails"; only one is a hole.
 */
export function sessionImages(payload) {
  const { at } = reader(payload);
  const raw = at('images');
  const ids = at('image_roles');
  const mats = at('material_roles');

  const urls = Array.isArray(raw) ? raw
    : (typeof raw === 'string' && !isUnresolved(raw) && raw.trim() ? [raw] : []);
  const roleIds = Array.isArray(ids) ? ids : [];
  const roles = Array.isArray(mats) ? mats : [];
  const declared = typeof raw === 'number' ? raw : 0;
  const count = Math.max(urls.length, roleIds.length, roles.length, declared);

  const items = [];
  for (let i = 0; i < count; i += 1) {
    const role = roles[i] && typeof roles[i] === 'object' ? roles[i] : null;
    const id = roleIds[i] ?? role?.id ?? null;
    const url = typeof urls[i] === 'string' ? urls[i] : (urls[i]?.url ?? null);
    items.push({
      index: i + 1,
      url: url && !isUnresolved(url) ? url : null,
      role: role?.label ?? (id != null ? String(id) : null),
      title: role?.title ?? null,
    });
  }
  return {
    items,
    picked: items.filter((x) => x.url).length,
    // WHY the slots are empty, when they are — the reference that did not
    // resolve, so "not on this page" never reads as "broken".
    pending: isUnresolved(raw) ? raw : null,
    pendingRoles: isUnresolved(ids) ? ids : null,
  };
}

/** What the model is told AROUND the prompt — the four switches that steer it. */
const INJECTION_KEYS = [
  ['prompt_policy', 'how literally the prompt is sent'],
  ['template_id', 'which template this run is'],
  ['image_roles', 'the role each photo fills, in order'],
  ['required_media_count', 'photos the session needs before it runs'],
];

/** Rendered by a section of their own — never repeated in the tail rows. */
const SHOWN_KEYS = new Set([
  'prompt', 'images', 'image_roles', 'prompt_policy', 'template_id',
  'required_media_count', 'material_roles', 'name', 'cover', 'args', 'payload',
]);

/**
 * EVERYTHING THE SECTIONS ABOVE DID NOT SHOW, still grouped by the question it
 * answers. `args` is FLATTENED — one row per key — because the single `args`
 * row is precisely the unreadable blob this view exists to retire; the keys
 * survive, the JSON wrapper does not.
 */
function restRows({ p, args }) {
  const rows = [];
  const own = new Set([...SHOWN_KEYS, ...CAPTURE_KEYS, ...BINDING_KEYS, ...DELEGATED_KEYS]);
  const add = (group, key, label, value) => {
    if (value !== undefined) rows.push(row(group, key, label, value));
  };
  for (const k of CAPTURE_KEYS) if (!SHOWN_KEYS.has(k)) add('capture', k, k, p[k]);
  for (const k of BINDING_KEYS) if (!SHOWN_KEYS.has(k)) add('binding', k, k, p[k]);
  for (const k of DELEGATED_KEYS) if (!SHOWN_KEYS.has(k)) add('delegated', k, k, p[k]);
  if (args) {
    for (const [k, v] of Object.entries(args)) {
      if (SHOWN_KEYS.has(k)) continue;
      rows.push(row('args', `args.${k}`, k, v));
    }
  } else if (p.args !== undefined) {
    add('args', 'args', 'args', p.args);
  }
  for (const k of Object.keys(p)) {
    if (own.has(k)) continue;
    add('extra', k, k, p[k]);
  }
  if (p.payload !== undefined) add('extra', 'payload', 'payload · also spread above', p.payload);
  return rows;
}

/** The whole readable page for one resolved payload. */
export function sessionSections(payload) {
  const parts = reader(payload);
  const { at } = parts;
  const id = at('template_id');
  const name = at('name');
  const cover = at('cover');
  const injections = [];
  for (const [key, hint] of INJECTION_KEYS) {
    const v = at(key);
    if (v === undefined) continue;
    injections.push({
      key,
      value: Array.isArray(v) ? v.map((x) => display(x)).join(', ') : display(v),
      hint,
      missing: isUnresolved(v),
    });
  }
  return {
    template: (id !== undefined || name !== undefined)
      ? { id: id === undefined ? null : display(id), name: name === undefined ? null : display(name), cover: isRenderableSrc(cover) ? cover : null }
      : null,
    prompt: promptOf(payload),
    injections,
    images: sessionImages(payload),
    rest: restRows(parts),
  };
}

/**
 * The options an OptionSelect emitter offers, when it AUTHORS them. A bound
 * `options: "modeFacets"` is the feed's, and the card says so rather than
 * inventing a list the blueprint does not carry.
 */
function optionsOf(node) {
  const raw = node?.props?.options;
  if (!Array.isArray(raw)) return null;
  return raw.filter(Boolean).map((o, i) => ({
    key: o.key ?? String(i),
    label: o.label ?? o.key ?? `option ${i + 1}`,
    payload: o.payload ?? null,
    value: o.value,
  }));
}

/** The signal an OptionSelect emits for one option (`filters.jsx` `pick`). */
function optionSignal(option) {
  if (!option) return {};
  const out = { key: option.key };
  if (option.value !== undefined) out.value = option.value;
  return out;
}

/**
 * The LADDER above the rows: what the app does with this payload, in the order
 * it does it. A delegated page is the long one — the payload is a preflight for
 * a capture that two WebView screens later dispatches — and reading it as one
 * step is exactly the misreading the dead chip encouraged.
 */
function ladderOf(kind, slot, file, delegated, native = null) {
  if (kind === 'delegated') {
    const pages = native instanceof Set ? native : new Set(native || []);
    const target = file ? String(file).replace(/\.html$/i, '') : null;
    // NATIVE now, WebView before: scene-studio's template_detail /
    // template_upload are pages of this bundle, so calling them "[WebView] the
    // delegated page" would name the one thing that stopped being true. The
    // rung says which surface actually draws the page, off the blueprint.
    const inBundle = target ? pages.has(target) : false;
    const upload = (inBundle ? [...pages] : [...delegated]).find((f) => /upload/i.test(f)) || null;
    return [
      `host.${slot} ${file || '(no file)'}`,
      inBundle ? `${target} · this bundle’s own page` : '[WebView] the delegated page',
      upload ? `Try → ${upload}` : 'Try',
      'Create scene → open_capture',
    ];
  }
  if (kind === 'capture') return [`host.${slot}`, 'app capture sheet', 'agent session'];
  if (kind === 'main') return [`host.${slot}`, 'the main chat session'];
  return [`host.${slot}`, 'the entry’s own capture session'];
}

/** What a partially resolved payload was resolved AGAINST — see `markMisses`. */
function groundOf(ctx, pageId, pageArgs) {
  const focus = ctx?.focusEntry || null;
  const rowKey = focus?.item_id ?? focus?.id ?? focus?.entry_id ?? null;
  // The KEYS, not the values: a template_upload page carries its whole locked
  // prompt in `page_args`, and serialising it here put a 4 KB line in the
  // header — above the prompt section that renders the same string readably.
  const keys = pageArgs ? Object.keys(pageArgs) : [];
  const args = keys.length
    ? `{${keys.slice(0, 6).join(', ')}${keys.length > 6 ? `, +${keys.length - 6}` : ''}}`
    : 'none';
  return {
    page: pageId || null,
    row: rowKey ? String(rowKey) : null,
    text: `resolved on page ${pageId || '?'} · page_args ${args} · focusEntry ${rowKey ? `#${rowKey}` : 'unresolved'}`,
  };
}

/**
 * THE WHOLE NODE, ready to render: which kind of session this is, the ladder,
 * the option switcher, and `rowsFor(key)` — the rows for one option, resolved
 * lazily so eleven template options are not eleven resolutions per paint.
 *
 * Returns null when the slot opens no session, which is what keeps
 * `crudRemove` / `imageTap` / a plain in-bundle `openPage` on the bridge chip
 * they were always right to be.
 */
export function sessionNode({
  blueprint, feed = {}, pageArgs = null, conn = null, slot = null,
  emitterId = null, uiSelected = undefined, pageId = null,
} = {}) {
  if (!slot || !SESSION_SLOTS.has(slot)) return null;
  const delegated = delegatedFiles(blueprint);
  const node = blueprint?.components?.[emitterId] || null;
  const options = optionsOf(node);
  const boundOptions = !options && node?.props?.options !== undefined
    ? String(node.props.options) : null;

  // WHICH OPTION THE READER CLICKED. The interaction store is keyed by node id
  // and holds the option KEY (`useSelection` in components/ui.js); with nothing
  // clicked the control's own seed decides, exactly as the component does.
  const seeded = node?.props?.selected;
  const activeKey = uiSelected !== undefined && uiSelected !== null ? uiSelected
    : (seeded !== undefined && seeded !== null ? seeded : options?.[0]?.key ?? null);
  const shownKey = activeKey ?? options?.[0]?.key ?? null;
  const untouched = uiSelected === undefined || uiSelected === null;

  const optionFor = (key) => (options || []).find((o) => String(o.key) === String(key)) || null;
  const kind = sessionKindOf(slot, optionFor(shownKey)?.payload ?? conn?.payload ?? null, delegated);
  if (!kind) return null;

  const rowsFor = (key) => {
    const option = optionFor(key);
    const { payload, ctx } = resolveSessionPayload({
      blueprint,
      feed,
      pageArgs,
      conn,
      optionPayload: option?.payload ?? null,
      signalShape: optionSignal(option),
    });
    return {
      payload,
      rows: injectionRows(payload),
      // …and the same payload read as a PAGE rather than as a key list. Both
      // are the one resolution: `view` never sees a value `rows` did not.
      view: sessionSections(payload),
      file: fileOf(payload),
      ground: groundOf(ctx, pageId, pageArgs),
    };
  };

  const nativePages = new Set((blueprint?.pages || []).map((p) => p.id).filter(Boolean));
  const file = fileOf(rowsFor(shownKey).payload);
  return {
    kind,
    slot,
    emitterId,
    emitterType: node?.type || null,
    options: options || [],
    boundOptions,
    activeKey: shownKey,
    untouched,
    // WHICH SESSION, and WHETHER IT STARTS HERE. A delegated preflight is the
    // one that does not: the tap opens a page CARRYING the session, and the
    // session itself is opened two screens later by that page's own button.
    // Reading the two as the same event is the misreading the dead chip fed.
    sessionType: sessionTypeOf(slot),
    startsHere: kind !== 'delegated',
    ladder: ladderOf(kind, slot, file, delegated, nativePages),
    rowsFor,
  };
}
